import { FC, useEffect, useState } from "react";
import { Input } from "@/components/UI";
import PhoneInput from "@/components/UI/PhoneInput";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/UI/Dialog/Dialog";
import { Button } from "@/components/UI/Buttons/Button";
import useForm from "@/custom_hooks/useForm";
import useNewslettersSubscription from "@/custom_hooks/useNewslettersSubcription";
import useMetaPixel from "@/custom_hooks/useMetaPixel";
import api from "@/services/api";
import { toast } from "sonner";
import { pushEvent } from "@/utils/gtm";
import { sendKlaviyoEvent } from "@/services/klaviyoService";
import { isPhoneValid } from "@/utils/formValidator";

interface SleepGuideModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const SleepGuideModal: FC<SleepGuideModalProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const metaPixel = useMetaPixel();

  const {
    formData,
    formMessages: { setErrorMsg, errorMsg, setSuccessMsg },
    onInputChange,
  } = useForm({ full_name: "", email: "", phone: "", phoneCountry: "LT" });

  const handlePhoneChange = (val: string) => {
    setErrorMsg("");
    onInputChange({
      target: { name: "phone", value: val },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handlePhoneCountryChange = (country: string | undefined) => {
    onInputChange({
      target: { name: "phoneCountry", value: country ?? "LT" },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const { ConfirmationCheckbox, isValid } = useNewslettersSubscription(
    setErrorMsg,
    setSuccessMsg,
    setIsLoading,
  );

  const downloadPDF = async () => {
    const response = await api.get("/documents/sleep-architecture", {
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "miego-gidas.pdf");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const isSleepGuideAd =
    new URLSearchParams(window.location.search).get("type") === "ad";

  useEffect(() => {
    if (isSleepGuideAd && isOpen) {
      pushEvent("sleep_guide_started");
      metaPixel.trackSleepGuideStarted(window.location.href);
    }
  }, [isSleepGuideAd, isOpen]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationError = isValid(formData.email);
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }
    if (!isPhoneValid(formData.phone)) {
      setErrorMsg("Neteisingas telefono numerio formatas");
      return;
    }
    if (!isPhoneValid(formData.phone, formData.phoneCountry)) {
      setErrorMsg("Telefono numeris neatitinka pasirinktos šalies");
      return;
    }

    setIsLoading(true);

    try {
      await api.post("/subscriptions", {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
      });

      metaPixel.trackSubscribeToNewsletter();

      if (isSleepGuideAd) {
        await sendKlaviyoEvent({
          email: formData.email,
          metric_type: "sleep_guide_ended",
          first_name: formData.full_name,
        });
        pushEvent("sleep_guide_ended");
        metaPixel.trackSleepGuideEnded(window.location.href);
      } else {
        pushEvent("newsletter_subscribe");
      }

      await downloadPDF();

      toast.success("Miego gidas sėkmingai atsisiųstas!");
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Įvyko klaida. Bandykite dar kartą.";
      setErrorMsg(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setErrorMsg("");
    onInputChange({
      target: { name: field, value },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-paper text-ink h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none border-0 p-6 sm:border-ink/15 sm:h-auto sm:w-[min(640px,95vw)] sm:max-w-[min(640px,95vw)] sm:max-h-[calc(100dvh-48px)] sm:rounded-[30px] sm:border sm:p-10">
        <DialogHeader className="mb-6 text-left">
          <DialogTitle className="text-ink text-[22px] leading-[1.25] font-semibold">
            Prisijunkite prie ilgaamžiškumo bendruomenės
          </DialogTitle>
          <DialogDescription className="text-ink/60 mt-2 text-[14px] leading-[1.5]">
            Užpildykite duomenis ir gaukite nemokamą miego gidą
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <Input
            label="Vardas ir pavardė*"
            name="full_name"
            value={formData.full_name}
            onChange={(e) => handleInputChange("full_name", e.target.value)}
            disableLabelClick
          />

          <Input
            label="El. paštas*"
            name="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            disableLabelClick
          />

          <PhoneInput
            label="Tel. numeris*"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handlePhoneChange}
            onCountryChange={handlePhoneCountryChange}
          />

          <ConfirmationCheckbox className="text-xs" />

          {errorMsg && (
            <div className="border-danger bg-canvas text-danger rounded-card border px-5 py-4 text-[14px] leading-[1.5]">
              {errorMsg}
            </div>
          )}

          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              variant="white"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="border-ink text-ink hover:bg-ink/5 h-[56px] flex-1 border bg-white text-[16px] font-normal shadow-none"
            >
              Atšaukti
            </Button>
            <Button
              type="submit"
              variant="ink"
              disabled={isLoading}
              className="h-[56px] flex-1 text-[16px] font-normal"
            >
              {isLoading ? "Siunčiama..." : "Gauti miego gidą"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SleepGuideModal;
